import "./style.css";
import { Bodies, World, type Body, type BodyOptions } from "fast-2d";
import { initAnalytics } from "./analytics";

const FIXED_DT = 1 / 60;
const MAX_CATCHUP_STEPS = 5;
const SCENE_SEED = 0xf25720d;
const INPUT_SEED = 0x10c575e9;
const MAX_DYNAMIC_BODIES = 42;

type BodyFactoryOptions = Omit<BodyOptions, "position">;

interface Pose {
	x: number;
	y: number;
	angle: number;
}

interface Client {
	world: World;
	dynamicBodies: Body[];
	previous: Map<Body, Pose>;
	current: Map<Body, Pose>;
}

type SharedInput =
	| {
			kind: "impulse";
			tick: number;
			target: number;
			impulseX: number;
			impulseY: number;
			offsetX: number;
			offsetY: number;
		}
	| {
			kind: "spawn";
			tick: number;
			x: number;
			y: number;
			shape: "box" | "circle";
			size: number;
			impulseX: number;
			impulseY: number;
		};

interface Viewport {
	x: number;
	y: number;
	width: number;
	height: number;
}

interface CameraState {
	centerX: number;
	centerY: number;
	scale: number;
}

interface PaneLayout {
	a: Viewport;
	b: Viewport;
	camera: CameraState;
}

const canvas = document.getElementById("lockstep-canvas") as HTMLCanvasElement;
const context = canvas.getContext("2d");
if (!context) throw new Error("Canvas 2D context is not available");
const ctx: CanvasRenderingContext2D = context;

const badge = document.getElementById("lockstep-badge") as HTMLElement;
const tickEl = document.getElementById("lockstep-tick") as HTMLElement;
const checksumAEl = document.getElementById("lockstep-checksum-a") as HTMLElement;
const checksumBEl = document.getElementById("lockstep-checksum-b") as HTMLElement;
const forceInput = document.getElementById("lockstep-force") as HTMLInputElement;
const resetButton = document.getElementById("lockstep-reset") as HTMLButtonElement;
const noteEl = document.getElementById("lockstep-note") as HTMLElement;
const eventsEl = document.getElementById("lockstep-events") as HTMLOListElement;

let clientA = buildClient();
let clientB = buildClient();
let tick = 0;
let accumulator = 0;
let lastTime = performance.now();
let checksumA = clientA.world.checksum();
let checksumB = clientB.world.checksum();
let desyncTick: number | null = null;
let pendingDivergence = false;
let queuedInputs: SharedInput[] = [];
const recentInputs: SharedInput[] = [];
let layout: PaneLayout = makeLayout(900, 520);

initAnalytics();
syncPoseBuffers(clientA);
syncPoseBuffers(clientB);
updateReadout();
renderEvents();

forceInput.addEventListener("change", () => {
	if (forceInput.checked) {
		pendingDivergence = true;
		noteEl.textContent = "Client B will receive one private micro-impulse on the next simulation tick.";
	} else {
		noteEl.textContent =
			desyncTick === null
				? "Divergence is disarmed. Click either pane to enqueue one shared spawn input for the next tick."
				: "Divergence is disarmed, but lockstep remains split until Reset both rebuilds both clients.";
	}
});

resetButton.addEventListener("click", () => {
	clientA = buildClient();
	clientB = buildClient();
	tick = 0;
	accumulator = 0;
	checksumA = clientA.world.checksum();
	checksumB = clientB.world.checksum();
	desyncTick = null;
	pendingDivergence = false;
	forceInput.checked = false;
	queuedInputs = [];
	recentInputs.length = 0;
	syncPoseBuffers(clientA);
	syncPoseBuffers(clientB);
	noteEl.textContent = "Both worlds rebuilt from the same seed. The shared input stream is empty again.";
	updateReadout();
	renderEvents();
});

canvas.addEventListener("pointerdown", (event) => {
	const rect = canvas.getBoundingClientRect();
	const x = event.clientX - rect.left;
	const y = event.clientY - rect.top;
	const worldPoint = pointToWorld(x, y, layout);
	if (!worldPoint) return;
	const input = makePointerSpawn(tick + 1, worldPoint.x, worldPoint.y);
	queuedInputs.push(input);
	recordInput(input);
	noteEl.textContent = `Queued shared spawn for tick ${input.tick}. Both clients will receive the same input.`;
});

requestAnimationFrame(loop);

function loop(now: number): void {
	const elapsed = Math.min((now - lastTime) / 1000, 0.1);
	lastTime = now;
	accumulator += elapsed;
	let steps = 0;
	while (accumulator >= FIXED_DT && steps < MAX_CATCHUP_STEPS) {
		stepOnce();
		accumulator -= FIXED_DT;
		steps++;
	}
	if (steps === MAX_CATCHUP_STEPS && accumulator > FIXED_DT) accumulator = 0;
	render(accumulator / FIXED_DT);
	requestAnimationFrame(loop);
}

function stepOnce(): void {
	const inputs = inputsForTick(tick);
	for (const input of inputs) {
		applyInput(clientA, input);
		applyInput(clientB, input);
	}
	clientA.world.step(FIXED_DT);
	if (pendingDivergence) {
		perturbClientB();
		pendingDivergence = false;
		noteEl.textContent = "Client B received a private micro-impulse. The checksum split is permanent until reset.";
	}
	clientB.world.step(FIXED_DT);
	tick++;
	syncPoseBuffers(clientA);
	syncPoseBuffers(clientB);
	checksumA = clientA.world.checksum();
	checksumB = clientB.world.checksum();
	if (checksumA !== checksumB && desyncTick === null) desyncTick = tick;
	updateReadout();
}

function inputsForTick(currentTick: number): SharedInput[] {
	const inputs: SharedInput[] = [];
	if (currentTick % 42 === 8) {
		const input = makeScriptedImpulse(currentTick);
		inputs.push(input);
		recordInput(input);
	}
	if (currentTick % 180 === 90) {
		const input = makeScriptedSpawn(currentTick);
		inputs.push(input);
		recordInput(input);
	}
	const stillQueued: SharedInput[] = [];
	for (const input of queuedInputs) {
		if (input.tick === currentTick) inputs.push(input);
		else stillQueued.push(input);
	}
	queuedInputs = stillQueued;
	return inputs;
}

function buildClient(): Client {
	const world = new World({
		gravity: [0, 9.81],
		fixedTimeStep: FIXED_DT,
		maxSubSteps: 8,
		sleep: false,
		solver: {
			substeps: 8,
			velocityIterations: 12,
			positionIterations: 8,
			contactHertz: 60,
			contactDampingRatio: 8,
			maxContactPushSpeed: 30,
		},
	});
	const dynamicBodies: Body[] = [];
	const random = rng(SCENE_SEED);

	addBox(world, 0, 3.65, 15.8, 4.8, { isStatic: true, friction: 0.9 });
	addBox(world, -8.15, -3.5, 0.5, 9.9, { isStatic: true, friction: 0.7 });
	addBox(world, 8.15, -3.5, 0.5, 9.9, { isStatic: true, friction: 0.7 });
	addBox(world, -4.1, -1.95, 4.1, 0.22, { isStatic: true, angle: 0.21, friction: 0.75 });
	addBox(world, 3.9, -2.95, 4.3, 0.22, { isStatic: true, angle: -0.22, friction: 0.75 });
	addBox(world, -0.15, -4.75, 3.2, 0.22, { isStatic: true, angle: 0.11, friction: 0.75 });

	for (let i = 0; i < 26; i++) {
		const column = i % 9;
		const row = Math.floor(i / 9);
		const x = -5.1 + column * 1.25 + (random() - 0.5) * 0.16;
		const y = -8.1 + row * 0.75 + (random() - 0.5) * 0.16;
		if (i % 3 === 1) {
			dynamicBodies.push(
				addCircle(world, x, y, 0.32 + random() * 0.08, {
					friction: 0.38,
					restitution: 0.42,
					density: 1 + random() * 0.3,
				}),
			);
		} else {
			dynamicBodies.push(
				addBox(world, x, y, 0.62, 0.62, {
					angle: (random() - 0.5) * 0.55,
					friction: 0.52,
					restitution: 0.22,
					density: 1.1,
				}),
			);
		}
	}

	return { world, dynamicBodies, previous: new Map(), current: new Map() };
}

function addBox(
	world: World,
	x: number,
	y: number,
	width: number,
	height: number,
	options: BodyFactoryOptions = {},
): Body {
	return world.add(Bodies.rectangle({ position: [x, y], width, height, ...options }));
}

function addCircle(
	world: World,
	x: number,
	y: number,
	radius: number,
	options: BodyFactoryOptions = {},
): Body {
	return world.add(Bodies.circle({ position: [x, y], radius, ...options }));
}

function applyInput(client: Client, input: SharedInput): void {
	if (input.kind === "impulse") {
		const bodies = client.dynamicBodies;
		if (bodies.length === 0) return;
		const body = bodies[input.target % bodies.length]!;
		body.applyImpulse(
			[input.impulseX, input.impulseY],
			[body.position.x + input.offsetX, body.position.y + input.offsetY],
		);
		return;
	}

	if (client.dynamicBodies.length >= MAX_DYNAMIC_BODIES) {
		const body = client.dynamicBodies[input.tick % client.dynamicBodies.length]!;
		body.applyImpulse([input.impulseX, input.impulseY], [body.position.x, body.position.y]);
		return;
	}

	const body =
		input.shape === "circle"
			? addCircle(client.world, input.x, input.y, input.size * 0.5, {
					friction: 0.35,
					restitution: 0.48,
					density: 1.05,
				})
			: addBox(client.world, input.x, input.y, input.size, input.size, {
					angle: (hash32(input.tick) / 0xffffffff - 0.5) * 0.4,
					friction: 0.5,
					restitution: 0.28,
					density: 1.15,
				});
	body.applyImpulse([input.impulseX, input.impulseY], [input.x, input.y]);
	client.dynamicBodies.push(body);
}

function perturbClientB(): void {
	const bodies = clientB.dynamicBodies;
	if (bodies.length === 0) return;
	const body = bodies[(tick * 7 + 5) % bodies.length]!;
	body.applyImpulse([0.00041, -0.00037], [body.position.x + 0.17, body.position.y - 0.09]);
}

function makeScriptedImpulse(currentTick: number): SharedInput {
	const h = hash32((currentTick + 1) ^ INPUT_SEED);
	return {
		kind: "impulse",
		tick: currentTick,
		target: h,
		impulseX: (((h >>> 8) & 0xff) / 255 - 0.5) * 2.8,
		impulseY: -1.9 - (((h >>> 16) & 0xff) / 255) * 2.5,
		offsetX: (((h >>> 24) & 0xff) / 255 - 0.5) * 0.38,
		offsetY: -0.12,
	};
}

function makeScriptedSpawn(currentTick: number): SharedInput {
	const h = hash32(currentTick ^ INPUT_SEED ^ 0xa53a9b1d);
	return {
		kind: "spawn",
		tick: currentTick,
		x: -5.7 + ((h & 0xff) / 255) * 11.4,
		y: -8.35 - (((h >>> 8) & 0xff) / 255) * 0.9,
		shape: (h & 1) === 0 ? "box" : "circle",
		size: 0.52 + (((h >>> 16) & 0xff) / 255) * 0.18,
		impulseX: (((h >>> 24) & 0xff) / 255 - 0.5) * 1.7,
		impulseY: -0.9,
	};
}

function makePointerSpawn(inputTick: number, x: number, y: number): SharedInput {
	const h = hash32(inputTick ^ Math.round((x + 20) * 101) ^ Math.round((y + 20) * 997));
	return {
		kind: "spawn",
		tick: inputTick,
		x: clamp(x, -6.6, 6.6),
		y: clamp(y, -8.8, -2.2),
		shape: (h & 1) === 0 ? "box" : "circle",
		size: 0.56,
		impulseX: (((h >>> 8) & 0xff) / 255 - 0.5) * 1.4,
		impulseY: -1.2,
	};
}

function syncPoseBuffers(client: Client): void {
	const nextCurrent = new Map<Body, Pose>();
	for (const body of client.world.bodies) {
		const pose = poseOf(body);
		client.previous.set(body, client.current.get(body) ?? pose);
		nextCurrent.set(body, pose);
	}
	client.current = nextCurrent;
}

function poseOf(body: Body): Pose {
	return { x: body.position.x, y: body.position.y, angle: body.angle };
}

function render(alpha: number): void {
	resizeCanvas();
	const scale = deviceScale();
	const width = canvas.width / scale;
	const height = canvas.height / scale;
	layout = makeLayout(width, height);
	ctx.save();
	ctx.scale(scale, scale);
	ctx.clearRect(0, 0, width, height);
	ctx.fillStyle = "#0a0c13";
	ctx.fillRect(0, 0, width, height);
	drawPane(clientA, "Client A", checksumA, layout.a, layout.camera, alpha, true);
	drawPane(clientB, "Client B", checksumB, layout.b, layout.camera, alpha, checksumA === checksumB);
	drawDivider(width, height, layout);
	ctx.restore();
}

function drawPane(
	client: Client,
	label: string,
	checksum: number,
	viewport: Viewport,
	camera: CameraState,
	alpha: number,
	inSync: boolean,
): void {
	ctx.save();
	ctx.beginPath();
	ctx.rect(viewport.x, viewport.y, viewport.width, viewport.height);
	ctx.clip();
	ctx.translate(viewport.x, viewport.y);
	ctx.fillStyle = "#0a0c13";
	ctx.fillRect(0, 0, viewport.width, viewport.height);
	drawGrid(viewport, camera);
	for (let i = 0; i < client.world.bodies.length; i++) {
		drawBody(client.world.bodies[i]!, client, viewport, camera, alpha, i + 1);
	}
	drawClientPlate(label, checksum, inSync);
	ctx.restore();
}

function drawGrid(viewport: Viewport, camera: CameraState): void {
	const localViewport = viewportLocal(viewport);
	const x0 = Math.floor(screenToWorldX(0, localViewport, camera));
	const x1 = Math.ceil(screenToWorldX(localViewport.width, localViewport, camera));
	const y0 = Math.floor(screenToWorldY(0, localViewport, camera));
	const y1 = Math.ceil(screenToWorldY(localViewport.height, localViewport, camera));
	ctx.lineWidth = 1;
	ctx.strokeStyle = "rgba(255,255,255,0.045)";
	ctx.beginPath();
	for (let x = x0; x <= x1; x++) {
		const sx = worldToScreenX(x, localViewport, camera);
		ctx.moveTo(sx, 0);
		ctx.lineTo(sx, localViewport.height);
	}
	for (let y = y0; y <= y1; y++) {
		const sy = worldToScreenY(y, localViewport, camera);
		ctx.moveTo(0, sy);
		ctx.lineTo(localViewport.width, sy);
	}
	ctx.stroke();
}

function drawBody(
	body: Body,
	client: Client,
	viewport: Viewport,
	camera: CameraState,
	alpha: number,
	colorKey: number,
): void {
	const localViewport = viewportLocal(viewport);
	const previous = client.previous.get(body) ?? poseOf(body);
	const current = client.current.get(body) ?? previous;
	const pose = {
		x: lerp(previous.x, current.x, alpha),
		y: lerp(previous.y, current.y, alpha),
		angle: lerpAngle(previous.angle, current.angle, alpha),
	};
	const fill = body.isStatic ? "#2b3145" : `hsl(${(colorKey * 47) % 360} 68% 58%)`;
	const stroke = body.isStatic ? "#3c4461" : "rgba(0,0,0,0.45)";
	ctx.fillStyle = fill;
	ctx.strokeStyle = stroke;
	ctx.lineWidth = 1.5;

	for (const part of body.parts) {
		if (part.isCircle) {
			const c = transformPoint(part.localCenterX, part.localCenterY, pose);
			const cx = worldToScreenX(c.x, localViewport, camera);
			const cy = worldToScreenY(c.y, localViewport, camera);
			const radius = part.radius * camera.scale;
			ctx.beginPath();
			ctx.arc(cx, cy, radius, 0, Math.PI * 2);
			ctx.fill();
			ctx.stroke();
			ctx.beginPath();
			ctx.moveTo(cx, cy);
			ctx.lineTo(cx + Math.cos(pose.angle) * radius, cy + Math.sin(pose.angle) * radius);
			ctx.strokeStyle = "rgba(0,0,0,0.35)";
			ctx.stroke();
			ctx.strokeStyle = stroke;
			continue;
		}

		const vertices = part.localVerts;
		ctx.beginPath();
		for (let i = 0; i < part.vertexCount; i++) {
			const offset = i * 2;
			const p = transformPoint(vertices[offset]!, vertices[offset + 1]!, pose);
			const sx = worldToScreenX(p.x, localViewport, camera);
			const sy = worldToScreenY(p.y, localViewport, camera);
			if (i === 0) ctx.moveTo(sx, sy);
			else ctx.lineTo(sx, sy);
		}
		ctx.closePath();
		ctx.fill();
		ctx.stroke();
	}
}

function viewportLocal(viewport: Viewport): Viewport {
	return { x: 0, y: 0, width: viewport.width, height: viewport.height };
}

function drawClientPlate(label: string, checksum: number, inSync: boolean): void {
	ctx.save();
	roundRect(ctx, 12, 12, 190, 52, 8);
	ctx.fillStyle = "rgba(8, 10, 16, 0.78)";
	ctx.fill();
	ctx.strokeStyle = inSync ? "rgba(22, 240, 180, 0.4)" : "rgba(255, 107, 107, 0.72)";
	ctx.stroke();
	ctx.fillStyle = "#e7eaf3";
	ctx.font = "700 13px ui-sans-serif, system-ui";
	ctx.fillText(label, 24, 33);
	ctx.fillStyle = "#9aa3bd";
	ctx.font = "12px ui-monospace, SFMono-Regular, Consolas, monospace";
	ctx.fillText(formatHex(checksum), 24, 52);
	ctx.restore();
}

function drawDivider(width: number, height: number, currentLayout: PaneLayout): void {
	ctx.strokeStyle = "rgba(255, 255, 255, 0.08)";
	ctx.lineWidth = 1;
	if (width >= 760) {
		const x = currentLayout.a.x + currentLayout.a.width + 6;
		ctx.beginPath();
		ctx.moveTo(x, currentLayout.a.y);
		ctx.lineTo(x, height - 12);
		ctx.stroke();
	} else {
		const y = currentLayout.a.y + currentLayout.a.height + 6;
		ctx.beginPath();
		ctx.moveTo(12, y);
		ctx.lineTo(width - 12, y);
		ctx.stroke();
	}
}

function makeLayout(width: number, height: number): PaneLayout {
	const gutter = 12;
	const compact = width < 760;
	if (compact) {
		const paneHeight = Math.max(220, (height - gutter) / 2);
		const pane = { x: 0, y: 0, width, height: paneHeight };
		const scale = Math.max(24, Math.min((width - 30) / 16.6, (paneHeight - 32) / 10.4));
		return {
			a: pane,
			b: { ...pane, y: paneHeight + gutter },
			camera: { centerX: 0, centerY: -3.35, scale },
		};
	}
	const paneWidth = (width - gutter) / 2;
	const pane = { x: 0, y: 0, width: paneWidth, height };
	const scale = Math.max(24, Math.min((paneWidth - 30) / 16.6, (height - 34) / 10.4));
	return {
		a: pane,
		b: { ...pane, x: paneWidth + gutter },
		camera: { centerX: 0, centerY: -3.35, scale },
	};
}

function pointToWorld(x: number, y: number, currentLayout: PaneLayout): { x: number; y: number } | null {
	for (const viewport of [currentLayout.a, currentLayout.b]) {
		if (
			x >= viewport.x &&
			x <= viewport.x + viewport.width &&
			y >= viewport.y &&
			y <= viewport.y + viewport.height
		) {
			const localViewport = viewportLocal(viewport);
			return {
				x: screenToWorldX(x - viewport.x, localViewport, currentLayout.camera),
				y: screenToWorldY(y - viewport.y, localViewport, currentLayout.camera),
			};
		}
	}
	return null;
}

function updateReadout(): void {
	const synced = checksumA === checksumB;
	badge.textContent = synced ? "IN SYNC ✓" : "DESYNC ✗";
	badge.classList.toggle("lockstep-badge-sync", synced);
	badge.classList.toggle("lockstep-badge-desync", !synced);
	tickEl.textContent = String(tick);
	checksumAEl.textContent = formatHex(checksumA);
	checksumBEl.textContent = formatHex(checksumB);
	if (!synced && desyncTick !== null) {
		noteEl.textContent = `Desync detected at tick ${desyncTick}. Reset both is the lockstep recovery path for this demo.`;
	}
}

function recordInput(input: SharedInput): void {
	recentInputs.unshift(input);
	if (recentInputs.length > 7) recentInputs.pop();
	renderEvents();
}

function renderEvents(): void {
	eventsEl.replaceChildren();
	if (recentInputs.length === 0) {
		const empty = document.createElement("li");
		empty.textContent = "No inputs yet. Scripted impulses begin automatically, or click either pane.";
		eventsEl.appendChild(empty);
		return;
	}
	for (const input of recentInputs) {
		const item = document.createElement("li");
		item.textContent = describeInput(input);
		eventsEl.appendChild(item);
	}
}

function describeInput(input: SharedInput): string {
	if (input.kind === "impulse") {
		return `tick ${input.tick}: impulse target #${input.target % Math.max(1, clientA.dynamicBodies.length)} (${input.impulseX.toFixed(2)}, ${input.impulseY.toFixed(2)})`;
	}
	return `tick ${input.tick}: spawn ${input.shape} at (${input.x.toFixed(2)}, ${input.y.toFixed(2)})`;
}

function resizeCanvas(): void {
	const rect = canvas.getBoundingClientRect();
	const scale = deviceScale();
	const width = Math.max(1, Math.round(rect.width * scale));
	const height = Math.max(1, Math.round(rect.height * scale));
	if (canvas.width !== width || canvas.height !== height) {
		canvas.width = width;
		canvas.height = height;
	}
}

function deviceScale(): number {
	return Math.min(window.devicePixelRatio || 1, 2);
}

function worldToScreenX(x: number, viewport: Viewport, camera: CameraState): number {
	return (x - camera.centerX) * camera.scale + viewport.width / 2;
}

function worldToScreenY(y: number, viewport: Viewport, camera: CameraState): number {
	return (y - camera.centerY) * camera.scale + viewport.height / 2;
}

function screenToWorldX(x: number, viewport: Viewport, camera: CameraState): number {
	return (x - viewport.width / 2) / camera.scale + camera.centerX;
}

function screenToWorldY(y: number, viewport: Viewport, camera: CameraState): number {
	return (y - viewport.height / 2) / camera.scale + camera.centerY;
}

function transformPoint(x: number, y: number, pose: Pose): { x: number; y: number } {
	const c = Math.cos(pose.angle);
	const s = Math.sin(pose.angle);
	return { x: pose.x + x * c - y * s, y: pose.y + x * s + y * c };
}

function lerp(a: number, b: number, alpha: number): number {
	return a + (b - a) * alpha;
}

function lerpAngle(a: number, b: number, alpha: number): number {
	let delta = b - a;
	if (delta > Math.PI) delta -= Math.PI * 2;
	else if (delta < -Math.PI) delta += Math.PI * 2;
	return a + delta * alpha;
}

function clamp(value: number, min: number, max: number): number {
	return Math.max(min, Math.min(max, value));
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
	ctx.beginPath();
	ctx.moveTo(x + r, y);
	ctx.arcTo(x + w, y, x + w, y + h, r);
	ctx.arcTo(x + w, y + h, x, y + h, r);
	ctx.arcTo(x, y + h, x, y, r);
	ctx.arcTo(x, y, x + w, y, r);
	ctx.closePath();
}

function formatHex(value: number): string {
	return `0x${(value >>> 0).toString(16).padStart(8, "0").toUpperCase()}`;
}

function hash32(value: number): number {
	let x = value >>> 0;
	x ^= x >>> 16;
	x = Math.imul(x, 0x7feb352d);
	x ^= x >>> 15;
	x = Math.imul(x, 0x846ca68b);
	x ^= x >>> 16;
	return x >>> 0;
}

function rng(seed: number): () => number {
	let state = seed >>> 0;
	return () => {
		state = (state + 0x6d2b79f5) | 0;
		let value = Math.imul(state ^ (state >>> 15), 1 | state);
		value = (value + Math.imul(value ^ (value >>> 7), 61 | value)) ^ value;
		return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
	};
}